'use client';

import { c as _c } from "react/compiler-runtime";
import { Plus, Xmark } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { SimpleSpinner } from '~/components/Spinner/Spinner';
import { useTripOverviewSummary } from '~/components/TripOverview/hooks/useTripOverviewSummary';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { useToast } from '~/hooks/useToasts';
import { api } from '~/libs/api';
import type { TripOverviewItem } from '~/services/engine-ts';
import styles from './TripBoundaryStay.module.css';
type TripBoundaryStayProps = {
  position: 'start' | 'end';
};
const getPlaceName = (item: TripOverviewItem | undefined, position: TripBoundaryStayProps['position']) => {
  if (!item) return '';
  if (item.type !== 'leg') return item.place.name;
  return position === 'start' ? item.origin.name : item.destination.name;
};
export const TripBoundaryStay = t0 => {
  const $ = _c(13);
  const {
    position
  } = t0;
  const utils = api.useUtils();
  const t = useTranslations("journey");
  const tFeedback = useTranslations("content.feedback");
  const {
    tripId,
    firstItem,
    lastItem,
    isDateUnset,
    canEditTripSelections,
    isLiveChecking
  } = useTripOverviewSummary();
  const isTripUpdating = useIsTripUpdating();
  const toast = useToast();
  const atStart = position === "start";
  const boundaryItem = atStart ? firstItem : lastItem;
  const hasStay = boundaryItem?.type === "stay";
  const placeName = getPlaceName(boundaryItem, position);
  let t1;
  if ($[0] !== tripId || $[1] !== utils) {
    t1 = () => Promise.all([utils.trip.getOverview.invalidate({
      tripId
    }), utils.trip.getPrice.invalidate({
      tripId
    }), utils.trip.getAccommodation.invalidate({
      tripId
    }), utils.trip.getTransport.invalidate({
      tripId
    }), utils.trip.getTransportTimes.invalidate({
      tripId
    }), utils.trip.liveCheck.reset({
      tripId,
      replaceUnavailable: false
    })]);
    $[0] = tripId;
    $[1] = utils;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const refreshTrip = t1;
  let t2;
  if ($[3] !== toast) {
    t2 = (title, variant) => toast?.add({
      title,
      variant,
      position: "middle"
    }, {
      timeout: 5000
    });
    $[3] = toast;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const showToast = t2;
  const addStay = api.trip.addStay.useMutation({
    onSuccess: async result => {
      if (!result.success) {
        showToast(tFeedback("stay_add_error_dynamic", {
          place: placeName
        }), "error");
        return;
      }
      await refreshTrip();
      showToast(tFeedback("stay_add_success_dynamic", {
        place: placeName
      }), "success");
    },
    onError: () => showToast(tFeedback("stay_add_error_dynamic", {
      place: placeName
    }), "error")
  });
  const removeStay = api.trip.removeStay.useMutation({
    onSuccess: async result_0 => {
      if (!result_0.success) {
        showToast(tFeedback("stay_remove_error_dynamic", {
          place: placeName
        }), "error");
        return;
      }
      await refreshTrip();
      showToast(tFeedback("stay_remove_success_dynamic", {
        place: placeName
      }), "success");
    },
    onError: () => showToast(tFeedback("stay_remove_error_dynamic", {
      place: placeName
    }), "error")
  });
  if (!canEditTripSelections || isDateUnset || !firstItem || !lastItem || firstItem === lastItem) {
    return null;
  }
  const isPending = addStay.isPending || removeStay.isPending;
  const isDisabled = isPending || isLiveChecking || isTripUpdating;
  const t3 = !atStart;
  let t4;
  if ($[5] !== atStart || $[6] !== t3 || $[7] !== tripId) {
    t4 = {
      journeyId: tripId,
      atStart,
      atEnd: t3
    };
    $[5] = atStart;
    $[6] = t3;
    $[7] = tripId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const variables = t4;
  const renderPendingLabel = _temp;
  const t5 = styles;
  const t6 = `trip-${position}-stay`;
  const t7 = hasStay ? <button type="button" className={styles.removeButton} disabled={isDisabled} onClick={() => removeStay.mutate(variables)} data-test-id={`remove-${position}-stay`}>{removeStay.isPending ? renderPendingLabel(t("removing_stay")) : <><Xmark decorative={true} />{t("remove_stay")}</>}</button> : <><h5 className={styles.heading}>{t("add_place_accommodation", {
        place: placeName
      })}</h5><button type="button" className={styles.addButton} disabled={isDisabled} onClick={() => addStay.mutate(variables)} data-test-id={`add-${position}-stay`}>{addStay.isPending ? renderPendingLabel(t("adding_stay")) : <><Plus decorative={true} />{t("add_stay")}</>}</button>{addStay.isPending && <div className={styles.skeleton} aria-hidden={true} />}</>;
  let t8;
  if ($[9] !== t5.wrapper || $[10] !== t6 || $[11] !== t7) {
    t8 = <div className={t5.wrapper} data-test-id={t6}>{t7}</div>;
    $[9] = t5.wrapper;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
function _temp(label) {
  return <span className={styles.pendingLabel}><SimpleSpinner aria-hidden={true} width={18} height={18} />{label}</span>;
}